import { cn } from '@/lib/utils';
import {
  APP_CATEGORY_OPTIONS,
  APP_MAX_CATEGORIES,
  appCategoryLabel,
  toggleAppCategory,
} from '@/lib/app-categories';

interface AppCategoryFieldsProps {
  /** The chosen category ids, in order: the first is the primary one. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Distinguishes the hint's id when two forms are on one page. */
  idPrefix?: string;
  disabled?: boolean;
}

/**
 * The Arche Store category chips for an app (not a game — a game names
 * genres), shared by the Arche Builder, Fias Forge and the Store listings page
 * so all three ask the same question the same way. The first one picked is the
 * primary category: it shows under the app's name and is the one it is ranked
 * in.
 */
export function AppCategoryFields({
  value,
  onChange,
  idPrefix = 'arche',
  disabled = false,
}: AppCategoryFieldsProps) {
  const hintId = `${idPrefix}-app-categories-hint`;
  const atLimit = value.length >= APP_MAX_CATEGORIES;
  const primary = value[0];

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Arche Store categories</p>
      <p id={hintId} className="text-xs text-muted-foreground">
        Choose up to {APP_MAX_CATEGORIES}. The first is the primary one, shown under the name.
        {primary && (
          <>
            {' '}
            Primary: <span className="font-medium">{appCategoryLabel(primary)}</span>.
          </>
        )}
      </p>
      <div
        role="group"
        aria-label="Arche Store categories"
        aria-describedby={hintId}
        className="flex flex-wrap gap-2"
      >
        {APP_CATEGORY_OPTIONS.map((category) => {
          const selected = value.includes(category.id);
          const unavailable = disabled || (!selected && atLimit);
          return (
            <button
              key={category.id}
              type="button"
              aria-pressed={selected}
              disabled={unavailable}
              onClick={() => onChange(toggleAppCategory(value, category.id))}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                selected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background hover:bg-muted',
                unavailable && !selected && 'cursor-not-allowed opacity-50',
              )}
            >
              {category.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
